'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CartState, CartItem, OrderType, SelectedOption } from '@/types';

interface SavedAddress {
  street: string;
  city: string;
  phone?: string;
  apartment?: string;
  floor?: string;
  deliveryInstructions?: string;
}

interface CartContextType {
  cart: CartState;
  savedAddress: SavedAddress | null;
  customerNote: string;
  selectedOrderOptions: Record<string, any>;
  addToCart: (item: CartItem) => void;
  removeFromCart: (index: number) => void;
  updateQuantity: (index: number, quantity: number) => void;
  updateCartItem: (index: number, item: CartItem) => void;
  clearCart: () => void;
  setOrderType: (type: OrderType) => void;
  setGuestInfo: (info: { name: string; phone: string; email?: string }) => void;
  setSavedAddress: (address: SavedAddress | null) => void;
  setCustomerNote: (note: string) => void;
  setSelectedOrderOptions: (options: Record<string, any>) => void;
  restoreOrder: (items: CartItem[], address: SavedAddress | null, note: string, orderType: OrderType) => void;
  getCartTotal: () => number;
  getCartItemsCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'amam_cart';
const ADDRESS_STORAGE_KEY = 'amam_cart_address';
const NOTE_STORAGE_KEY = 'amam_cart_note';
const ORDER_OPTIONS_STORAGE_KEY = 'amam_cart_order_options';

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartState>({
    orderType: OrderType.DELIVERY,
    items: [],
  });
  const [savedAddress, setSavedAddressState] = useState<SavedAddress | null>(null);
  const [customerNote, setCustomerNoteState] = useState<string>('');
  const [selectedOrderOptions, setSelectedOrderOptionsState] = useState<Record<string, any>>({});

  const [isLoaded, setIsLoaded] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        try {
          setCart(JSON.parse(savedCart));
        } catch (error) {
          console.error('Failed to parse cart from localStorage:', error);
        }
      }
      const savedAddr = localStorage.getItem(ADDRESS_STORAGE_KEY);
      if (savedAddr) {
        try {
          setSavedAddressState(JSON.parse(savedAddr));
        } catch (error) {
          console.error('Failed to parse address from localStorage:', error);
        }
      }
      const savedNote = localStorage.getItem(NOTE_STORAGE_KEY);
      if (savedNote) {
        setCustomerNoteState(savedNote);
      }
      const savedOrderOptions = localStorage.getItem(ORDER_OPTIONS_STORAGE_KEY);
      if (savedOrderOptions) {
        try {
          setSelectedOrderOptionsState(JSON.parse(savedOrderOptions));
        } catch (error) {
          console.error('Failed to parse order options from localStorage:', error);
        }
      }
      setIsLoaded(true);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (isLoaded && typeof window !== 'undefined') {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
      if (savedAddress) {
        localStorage.setItem(ADDRESS_STORAGE_KEY, JSON.stringify(savedAddress));
      } else {
        localStorage.removeItem(ADDRESS_STORAGE_KEY);
      }
      localStorage.setItem(NOTE_STORAGE_KEY, customerNote);
      localStorage.setItem(ORDER_OPTIONS_STORAGE_KEY, JSON.stringify(selectedOrderOptions));
    }
  }, [cart, savedAddress, customerNote, selectedOrderOptions, isLoaded]);

  const addToCart = (item: CartItem) => {
    setCart((prev) => ({
      ...prev,
      items: [...prev.items, item],
    }));
  };

  const removeFromCart = (index: number) => {
    setCart((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }

    setCart((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const itemTotal = item.unitPrice * quantity;
          return { ...item, quantity, itemTotal };
        }
        return item;
      }),
    }));
  };

  const updateCartItem = (index: number, item: CartItem) => {
    setCart((prev) => ({
      ...prev,
      items: prev.items.map((existingItem, i) => {
        if (i === index) {
          return item;
        }
        return existingItem;
      }),
    }));
  };

  const clearCart = () => {
    setCart({
      orderType: OrderType.DELIVERY,
      items: [],
    });
    setSavedAddressState(null);
    setCustomerNoteState('');
    setSelectedOrderOptionsState({});
  };

  const setSavedAddress = (address: SavedAddress | null) => {
    setSavedAddressState(address);
  };

  const setCustomerNote = (note: string) => {
    setCustomerNoteState(note);
  };

  const setSelectedOrderOptions = (options: Record<string, any>) => {
    setSelectedOrderOptionsState(options);
  };

  const restoreOrder = (items: CartItem[], address: SavedAddress | null, note: string, orderType: OrderType) => {
    setCart({
      orderType,
      items,
    });
    setSavedAddressState(address);
    setCustomerNoteState(note);
  };

  const setOrderType = (type: OrderType) => {
    setCart((prev) => ({
      ...prev,
      orderType: type,
    }));
  };

  const setGuestInfo = (info: { name: string; phone: string; email?: string }) => {
    setCart((prev) => ({
      ...prev,
      guestInfo: info,
    }));
  };

  const getCartTotal = () => {
    return cart.items.reduce((total, item) => total + item.itemTotal, 0);
  };

  const getCartItemsCount = () => {
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        savedAddress,
        customerNote,
        selectedOrderOptions,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateCartItem,
        clearCart,
        setOrderType,
        setGuestInfo,
        setSavedAddress,
        setCustomerNote,
        setSelectedOrderOptions,
        restoreOrder,
        getCartTotal,
        getCartItemsCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
